import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Zap, Bot, BarChart3, Globe, Target, TrendingUp,
  Layers, Cpu, Database, Server, ArrowRight, ExternalLink,
  Users, DollarSign, Rocket, Shield
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }
  })
};

function useCountUp(target, duration = 1800, shouldStart = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!shouldStart || typeof target !== 'number') return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, shouldStart]);
  return count;
}

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

function StatCard({ num, suffix, label, prefix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  const count = useCountUp(num, 1800, inView);
  return (
    <div ref={ref} className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
      <div className="text-3xl font-black text-white mb-1">
        {prefix}{inView ? count.toLocaleString() : 0}{suffix}
      </div>
      <div className="text-sm text-gray-400 font-medium">{label}</div>
    </div>
  );
}

function Chip({ children, blue }) {
  return (
    <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full border ${
      blue
        ? 'bg-blue-500/15 border-blue-500/30 text-blue-300'
        : 'bg-white/5 border-white/10 text-gray-300'
    }`}>{children}</span>
  );
}

function Section({ children, id, className = '' }) {
  return (
    <section id={id} className={`max-w-5xl mx-auto px-6 md:px-12 py-16 ${className}`}>
      {children}
    </section>
  );
}

function Label({ children }) {
  return <div className="text-xs font-bold text-blue-400 tracking-widest uppercase mb-3">{children}</div>;
}

function Heading({ children }) {
  return <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-3">{children}</h2>;
}

function Sub({ children }) {
  return <p className="text-gray-400 text-base mb-10 max-w-2xl">{children}</p>;
}

const GrantPitchDeck = () => {
  return (
    <div className="min-h-screen text-white grant-deck-root">
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 0; }
          html, body { background: #010414 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          /* Hide the Layout's animated background + grain overlays + nav/header so PDF is clean */
          body > div:not(.grant-deck-root):not(#root) { display: none !important; }
          .grant-deck-root section { break-after: page; page-break-after: always; min-height: 100vh; display: flex; flex-direction: column; justify-content: center; padding: 40px 60px !important; max-width: 100% !important; margin: 0 !important; box-sizing: border-box; }
          .grant-deck-root section:last-child { break-after: auto; page-break-after: auto; }
          .grant-deck-root section > div[style*="background"] { background: transparent !important; }
          /* Strip Framer Motion translate so animated content is visible in print */
          .grant-deck-root [style*="opacity"] { opacity: 1 !important; transform: none !important; }
        }
      `}</style>
      {/* ═══════════════════════════════════ SLIDE 1 — COVER */}
      <Section id="cover" className="pt-24 pb-12 text-center">
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <div className="inline-block bg-blue-500/15 border border-blue-500/30 text-blue-300 text-xs font-bold px-4 py-2 rounded-full tracking-widest uppercase mb-6">
            AI Content Distribution Platform
          </div>
          <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-none mb-6">
            Social<span className="text-blue-400">SEO</span> LLP
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8 leading-relaxed">
            India's AI infrastructure for content distribution — turning long-form founder content into
            1,000–5,000 viral clips, distributed across 9 platforms automatically.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mb-10">
            <Chip blue>DPIIT Recognition — In Process</Chip>
            <Chip>Incorporated Dec 31, 2025</Chip>
            <Chip>Noida, Uttar Pradesh</Chip>
            <Chip>100% Indian Ownership</Chip>
          </div>
          <div className="flex gap-4 justify-center flex-wrap">
            <a href="https://socialdeck.socialseo.in" target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-3 rounded-xl transition-colors text-sm">
              View Live Case Studies <ExternalLink size={14} />
            </a>
            <a href="mailto:suresh@socialseo.in"
               className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold px-6 py-3 rounded-xl transition-colors text-sm">
              Contact Founder <ArrowRight size={14} />
            </a>
          </div>
        </motion.div>
      </Section>

      {/* STATS BAR */}
      <div className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard num={12} suffix="L+" prefix="₹" label="Revenue — 4 months" />
          <StatCard num={6} suffix="M+" label="Total views (clients)" />
          <StatCard num={6000} suffix="" label="Videos/month capacity" />
          <StatCard num={6000} suffix="+" label="Content pieces tested" />
          <StatCard num={3} suffix="" label="Paying clients (live)" />
        </div>
      </div>

      {/* ═══════════════════════════════════ SLIDE 2 — PROBLEM */}
      <Section id="problem">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <Label>The Problem</Label>
          <Heading>₹5 Lakh/Year on Content. 200 Followers After 6 Months.</Heading>
          <Sub>India's 63 million MSMEs and founders are invisible online — not because they don't create, but because they can't distribute.</Sub>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: DollarSign, title: 'Unaffordable Cost', body: '₹100/video × 1,000 clips = ₹1L/month in manual production. Beyond reach for most founders and MSMEs.' },
            { icon: Layers, title: '9 Platforms, Zero Expertise', body: 'Instagram, LinkedIn, YouTube, TikTok each have different algorithm requirements. Most founders master zero of nine.' },
            { icon: Globe, title: '94% of Content Wasted', body: '94% of all long-form content produced (podcasts, webinars, interviews) is never repurposed into short-form distribution.' },
          ].map(({ icon: Icon, title, body }, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}
              className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="w-10 h-10 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center justify-center mb-4">
                <Icon size={18} className="text-red-400" />
              </div>
              <h3 className="font-bold text-white mb-2">{title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ═══════════════════════════════════ SLIDE 3 — SOLUTION */}
      <div className="border-y border-white/10 bg-white/[0.02]">
        <Section id="solution">
          <Label>The Solution</Label>
          <Heading>Social SEO AI Platform — 3 Layers</Heading>
          <Sub>One pipeline. Fully automated. From raw content to 9-platform viral distribution.</Sub>

          <div className="flex flex-col md:flex-row gap-4 mb-8 items-center justify-center text-center">
            {['INGEST', '→', 'PRODUCE', '→', 'DISTRIBUTE', '→', 'OPTIMIZE'].map((step, i) => (
              <div key={i} className={step === '→' ? 'text-blue-400 text-xl font-bold hidden md:block' :
                'bg-blue-500/10 border border-blue-500/20 rounded-xl px-5 py-3 text-sm font-bold text-blue-300 flex-1 max-w-[140px]'}>
                {step}
              </div>
            ))}
          </div>
          <p className="text-center text-gray-400 text-sm mb-10">
            1 hour of content → AI segmentation → 60–120 clips → 6,000+ monthly posts across all platforms
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: Server, color: 'blue', title: 'n8n Orchestration', desc: 'Self-hosted workflow automation connecting transcription, AI generation, and distribution. 6,000 videos/month throughput.' },
              { icon: Bot, color: 'purple', title: 'Claude API Intelligence', desc: 'Anthropic LLM generates 3-5 hook variants per clip, writes scripts in each founder\'s voice, classifies CRM emails.' },
              { icon: Globe, color: 'green', title: 'Fan Page Distribution', desc: '20-25 niche pages per client on Instagram, LinkedIn, YouTube, TikTok, Facebook, X, Threads, Pinterest, and Shorts.' },
            ].map(({ icon: Icon, color, title, desc }, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className={`w-10 h-10 bg-${color}-500/10 border border-${color}-500/20 rounded-xl flex items-center justify-center mb-4`}>
                  <Icon size={18} className={`text-${color}-400`} />
                </div>
                <h3 className="font-bold text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      </div>

      {/* ═══════════════════════════════════ SLIDE 5 — TECHNOLOGY */}
      <Section id="technology">
        <Label>Technology Stack</Label>
        <Heading>Built & Deployed in Production</Heading>
        <Sub>Every tool is live and generating revenue — not a prototype.</Sub>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { name: 'n8n (Self-hosted)', role: 'Content orchestration pipeline' },
            { name: 'Claude API (Anthropic)', role: 'Hook generation + script writing' },
            { name: 'Brand Audit SaaS', role: 'Next.js + ElevenLabs + Supabase' },
            { name: 'LinkedIn Operator', role: 'AI batch content pipeline' },
            { name: 'CRM Reply Tracker', role: 'IMAP + Notion API automation' },
            { name: 'Blotato API', role: 'Multi-platform distribution' },
            { name: 'Supabase (PostgreSQL)', role: 'Database + media storage' },
            { name: 'Production VPS', role: 'Ubuntu + Cloudflare Tunnel' },
            { name: 'React + Tailwind', role: 'Client reporting platform' },
          ].map(({ name, role }, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.5} variants={fadeUp}
              className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-start gap-3">
              <div className="w-2 h-2 bg-blue-400 rounded-full mt-1.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-bold text-white">{name}</div>
                <div className="text-xs text-gray-500 mt-0.5">{role}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ═══════════════════════════════════ SLIDE 6 — MARKET */}
      <div className="border-y border-white/10 bg-white/[0.02]">
        <Section id="market">
          <Label>Market Size</Label>
          <Heading>₹15,000 Crore+ Addressable Market</Heading>
          <Sub>India's creator economy + MSME content marketing spend is massive and underserved.</Sub>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              { label: 'TAM', val: '₹15,000 Cr+', desc: 'Total content services + SaaS market for Indian MSMEs and creators' },
              { label: 'SAM', val: '₹1,500 Cr', desc: 'AI-augmented content distribution for tech-savvy Indian founders in Tier 1/2 cities' },
              { label: 'SOM', val: '₹150 Cr', desc: 'Brand Audit + LinkedIn Operator SaaS for Delhi-NCR, Mumbai, Bengaluru ecosystem (Year 1-2)' },
            ].map(({ label, val, desc }, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                <div className="text-xs font-black text-blue-400 tracking-widest uppercase mb-2">{label}</div>
                <div className="text-3xl font-black text-white mb-3">{val}</div>
                <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-gray-500 text-sm mt-6">Sources: Redseer India Creator Economy 2024, MSME Ministry annual data</p>
        </Section>
      </div>

      {/* ═══════════════════════════════════ SLIDE 7 — TRACTION */}
      <Section id="traction">
        <Label>Traction</Label>
        <Heading>3 Clients. 6M+ Views. ₹12L Revenue.</Heading>
        <Sub>All results are documented, live, and accessible to grant reviewers.</Sub>

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {[
            {
              client: 'Kush Desai',
              type: 'B2B SaaS Founder',
              headline: '0 → 10,000 Followers in 90 Days',
              stats: [['4.29M', 'Total Views'], ['8', 'Viral Reels >100K'], ['₹1M+', 'B2B Pipeline']],
              url: 'https://socialdeck.socialseo.in/reports/kush/report4',
            },
            {
              client: 'Abhiveer',
              type: 'Creator & Podcaster',
              headline: '220% Follower Growth in 30 Days',
              stats: [['830K+', 'Views in 30 Days'], ['220%', 'Follower Growth'], ['9', 'Viral Reels']],
              url: 'https://socialdeck.socialseo.in/reports/abhiveer/30-day',
            },
            {
              client: 'Sachin Dahiya',
              type: 'EdTech Founder (@thesachindahiya)',
              headline: '889K Views in Single Month',
              stats: [['889K+', 'Monthly Views'], ['302K', 'Reach'], ['9', 'Platforms']],
              url: 'https://socialdeck.socialseo.in/reports/sachin/30-day',
            },
          ].map(({ client, type, headline, stats, url }, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 relative">
              <div className="absolute top-0 left-0 w-1 h-full bg-blue-500 rounded-l-2xl" />
              <div className="text-xs font-bold text-blue-400 uppercase tracking-wide mb-2">{client}</div>
              <div className="text-xs text-gray-500 mb-3">{type}</div>
              <div className="font-bold text-white text-base mb-4 leading-snug">{headline}</div>
              <div className="flex gap-4 flex-wrap">
                {stats.map(([num, lbl], j) => (
                  <div key={j}>
                    <div className="text-xl font-black text-blue-400">{num}</div>
                    <div className="text-xs text-gray-500">{lbl}</div>
                  </div>
                ))}
              </div>
              <a href={url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-blue-400 text-xs font-semibold mt-4 hover:underline">
                View case study <ExternalLink size={11} />
              </a>
            </motion.div>
          ))}
        </div>

        {/* Proprietary methodology callout */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-6 flex items-start gap-4">
          <BarChart3 size={24} className="text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-white mb-1">Proprietary Social SEO Methodology — 6,000+ Piece Dataset</div>
            <p className="text-sm text-gray-400">
              Hook retention measured across 6,000+ content pieces. Level 1 hooks: 22% retention. Level 3 hooks: 61% retention — a 178% improvement.
              This dataset cannot be replicated without running real campaigns at scale.
            </p>
          </div>
        </div>
      </Section>

      {/* ═══════════════════════════════════ SLIDE 8 — BUSINESS MODEL */}
      <div className="border-y border-white/10 bg-white/[0.02]">
        <Section id="model">
          <Label>Business Model</Label>
          <Heading>Agency Today → Platform Tomorrow</Heading>
          <Sub>The managed service validates the platform. The grant bridges the transition to SaaS.</Sub>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              { phase: 'Now (Live)', title: 'Managed Service', price: '₹3L–12L/month', margin: '30–46%', desc: 'Full-service content production and distribution. 3 active clients. Revenue: ₹12L+ in 4 months.' },
              { phase: 'Q3 2026', title: 'Platform + Operator', price: '₹50K–1.5L/month', margin: '60%+', desc: 'SaaS license + managed service hybrid. Brand Audit SaaS + LinkedIn Operator as paid products.' },
              { phase: '2027+', title: 'API & White Label', price: 'Custom licensing', margin: '80%+', desc: 'B2B API licensing to agencies and platforms. White-label Social SEO for enterprise marketing teams.' },
            ].map(({ phase, title, price, margin, desc }, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="text-xs font-bold text-blue-400 mb-3">{phase}</div>
                <h3 className="font-black text-white text-lg mb-1">{title}</h3>
                <div className="text-2xl font-black text-white mb-1">{price}</div>
                <div className="text-xs text-green-400 font-bold mb-4">Gross margin: {margin}</div>
                <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      </div>

      {/* ═══════════════════════════════════ SLIDE 9 — TEAM */}
      <Section id="team">
        <Label>Team</Label>
        <Heading>Built by a 20-Year-Old from Noida</Heading>
        <Sub>Who tested 6,000+ content pieces and generated ₹12L before any external capital.</Sub>

        <div className="grid md:grid-cols-2 gap-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <div className="text-2xl font-black text-white mb-1">Suresh Malani</div>
            <div className="text-blue-400 font-semibold text-sm mb-4">Founder & CEO — 80% | DPIN: 11458707</div>
            <div className="flex flex-wrap gap-2">
              {['Age 20', '₹12L at 2K followers', '6,000+ pieces tested', '350M reach campaign', '10K leads in 8 hours', 'Full-stack + AI engineer', 'Noida, UP'].map(t => (
                <Chip key={t} blue>{t}</Chip>
              ))}
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
            className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <div className="text-2xl font-black text-white mb-1">Ashish Kumawat</div>
            <div className="text-blue-400 font-semibold text-sm mb-4">Co-Founder & COO — 20% | DPIN: 11458708</div>
            <div className="flex flex-wrap gap-2">
              {['Operations', 'Client delivery', 'Team coordination'].map(t => (
                <Chip key={t}>{t}</Chip>
              ))}
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ═══════════════════════════════════ SLIDE 10 — ROADMAP */}
      <div className="border-y border-white/10 bg-white/[0.02]">
        <Section id="roadmap">
          <Label>Roadmap</Label>
          <Heading>12-Month Execution Plan</Heading>

          <div className="space-y-3">
            {[
              { q: 'Q2 2026 (Now)', items: ['DPIIT registration', 'Microsoft + Google cloud credits', 'AIIDE + Amity incubator outreach', 'Brand Audit SaaS multi-tenant build begins'] },
              { q: 'Q3 2026', items: ['Brand Audit SaaS v1.0 launch (5 beta users)', 'LinkedIn Operator SaaS private beta', 'UP StartInUP funds disbursed', 'SISFS incubator application submission'] },
              { q: 'Q4 2026', items: ['25 paying SaaS subscribers (₹1.25L MRR)', 'MeitY TIDE 2.0 grant in progress', 'Onboard 2 additional managed clients', 'IP patent filing'] },
              { q: 'Q1 2027', items: ['50 paying SaaS subscribers (₹2.5L MRR)', 'SAMRIDH co-investment deployment', 'Southeast Asia pilot (Singapore, Indonesia)', 'Seed round preparation'] },
            ].map(({ q, items }, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="bg-white/5 border border-white/10 rounded-xl p-5 flex gap-4">
                <div className="text-xs font-black text-blue-400 uppercase tracking-wide min-w-[80px] pt-0.5">{q}</div>
                <div className="flex flex-wrap gap-2">
                  {items.map(item => <Chip key={item}>{item}</Chip>)}
                </div>
              </motion.div>
            ))}
          </div>
        </Section>
      </div>

      {/* ═══════════════════════════════════ SLIDE 11 — CAPITAL ASK */}
      <Section id="ask">
        <Label>Capital Ask</Label>
        <Heading>₹4.2–4.5 Crore Accessible — Zero Equity</Heading>
        <Sub>Across government grants, state schemes, and cloud credits. All non-dilutive.</Sub>

        <div className="grid md:grid-cols-2 gap-3">
          {[
            { program: 'Google Cloud Credits', value: '$350,000 (~₹2.9Cr)', type: 'Cloud Credits', use: 'Vertex AI model training, Cloud Run, Firebase for SaaS frontend', status: 'Apply immediately' },
            { program: 'Microsoft Azure Credits', value: '$150,000 (~₹1.25Cr)', type: 'Cloud Credits', use: 'Azure VMs for n8n, Blob Storage for video assets', status: 'Apply immediately' },
            { program: 'UP StartInUP (5 schemes)', value: '₹32L total', type: 'State Grant', use: 'Sustenance + Prototype + Seed Capital + IP Subsidy + Events', status: 'After DPIIT' },
            { program: 'MeitY SAMRIDH', value: '₹40L', type: 'Federal Grant', use: 'Engineering team (3 hires) + SaaS marketing launch', status: 'After incubator' },
            { program: 'SISFS (Startup India Seed Fund)', value: '₹20L–50L', type: 'Federal Grant', use: 'AI engineer + cloud compute + IP filing', status: 'After incubator' },
            { program: 'MeitY TIDE 2.0', value: '₹11L', type: 'Federal Grant', use: '₹7L prototype + ₹4L EIR stipend', status: 'After incubator' },
          ].map(({ program, value, type, use, status }, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}
              className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="font-bold text-white text-sm">{program}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{type}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-black text-blue-400">{value}</div>
                  <div className={`text-xs font-semibold mt-0.5 ${
                    status === 'Apply immediately' ? 'text-green-400' :
                    status === 'After DPIIT' ? 'text-yellow-400' : 'text-gray-400'
                  }`}>{status}</div>
                </div>
              </div>
              <p className="text-xs text-gray-500">Use: {use}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ═══════════════════════════════════ SLIDE 12 — VISION */}
      <div className="border-t border-white/10 bg-blue-500/5">
        <Section id="vision" className="text-center">
          <Label>Vision</Label>
          <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight leading-tight mb-6 max-w-3xl mx-auto">
            Content Distribution Infrastructure for India's 63 Million MSMEs
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-10">
            Every Indian founder should have an AI content engine running 24/7 — without recording a single video.
            Social SEO LLP is building that infrastructure layer.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <a href="https://socialdeck.socialseo.in" target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8 py-3 rounded-xl transition-colors">
              View All Client Results <ExternalLink size={16} />
            </a>
            <a href="mailto:suresh@socialseo.in"
               className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 text-white font-semibold px-8 py-3 rounded-xl transition-colors">
              suresh@socialseo.in
            </a>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-md mx-auto text-left">
            <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Entity Details</div>
            {[
              ['Entity', 'Social SEO LLP'],
              ['Incorporated', 'December 31, 2025'],
              ['Location', 'Noida, UP — India'],
              ['Ownership', '100% Indian (Suresh 80% + Ashish 20%)'],
              ['Revenue', '₹12L+ (bootstrapped)'],
              ['External Funding', '₹0'],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-2 border-b border-white/5 last:border-0">
                <span className="text-xs text-gray-500">{k}</span>
                <span className="text-xs font-semibold text-white">{v}</span>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
};

export default GrantPitchDeck;
